import type { Message as MessageClass } from './message';
import type { ArtifactDescriptor } from './types';
import type { Fact } from '../llm/llm-input-types';
import {
  type ChatSentimentLogRecord,
  ChatSentimentMetricName,
} from './chat-sentiment-log';
import {
  buildLlmUsageByMessageId,
  type AssistantMessageLlmUsage,
} from './assistant-message-llm-usage';
import { applyReceiptsToToolParts } from './conversation-receipt-splice';
import type { Receipt } from '../llm/llm-input-types';
import type { ToolPart } from './message-parts';

export type ConversationInsightsToolCall = {
  toolCallId: string;
  toolName: string;
  messageId: string;
  position: number;
  state: string;
  input: unknown;
  output: unknown;
};

export type ConversationInsightsTurn = {
  turnIndex: number;
  userMessageId: string | null;
  userMessagePosition: number | null;
  toolCalls: ConversationInsightsToolCall[];
};

export type ConversationInsightsResult = {
  messageId: string;
  position: number;
  output: unknown;
  text: string | null;
};

export type ConversationInsightsSentiment = {
  label: string | null;
  score: number | null;
  intent: string | null;
  summary: string | null;
  completionStatus: string | null;
  completionSummary: string | null;
  observedAt: string | null;
  analysisId: string | null;
};

export type ConversationInsightsCompaction = {
  messageId: string;
  position: number;
  createdAt: string;
  content: string;
};

export type ConversationInsightsFact = {
  scope: string;
  text: string;
};

export type ConversationInsightsMetadata = {
  triggerSource: string;
  agentId: string | null;
  parentId: string | null;
  model: string | null;
};

export type ConversationInsightsAssistantMessageUsage =
  AssistantMessageLlmUsage;

export type ConversationInsightsCounts = {
  userMessages: number;
  assistantMessages: number;
  toolCalls: number;
};

export type ConversationInsights = {
  conversationId: string;
  parentToolCallId: string | null;
  facts: ConversationInsightsFact[];
  turns: ConversationInsightsTurn[];
  result: ConversationInsightsResult | null;
  artifacts: ArtifactDescriptor[];
  sentiment: ConversationInsightsSentiment | null;
  compactions: ConversationInsightsCompaction[];
  counts: ConversationInsightsCounts;
  metadata: ConversationInsightsMetadata;
  llmUsageByMessageId: Record<
    string,
    ConversationInsightsAssistantMessageUsage
  >;
  subAgents: ConversationInsights[];
};

export function foldSentimentLogRecords(
  records: readonly ChatSentimentLogRecord[]
): ConversationInsightsSentiment | null {
  if (records.length === 0) {
    return null;
  }

  let label: string | null = null;
  let score: number | null = null;
  let intent: string | null = null;
  let summary: string | null = null;
  let completionStatus: string | null = null;
  let completionSummary: string | null = null;
  let observedAt: string | null = null;
  let analysisId: string | null = null;

  for (const record of records) {
    observedAt = record.observedAt.toISOString();
    analysisId = record.analysisId;

    if (record.isSentimentLabelRecord()) {
      label = record.label();
      continue;
    }
    if (record.isSentimentScoreRecord()) {
      score = record.score();
      continue;
    }
    if (record.isIntentRecord()) {
      intent = record.intent();
      continue;
    }
    if (record.isSummaryRecord()) {
      summary = record.summary();
      continue;
    }
    if (record.isCompletionStatusRecord()) {
      completionStatus = record.completionStatus();
      continue;
    }
    if (record.isCompletionSummaryRecord()) {
      completionSummary = record.completionSummary();
    }
  }

  if (
    label === null &&
    score === null &&
    intent === null &&
    summary === null &&
    completionStatus === null &&
    completionSummary === null
  ) {
    return null;
  }

  return {
    label,
    score,
    intent,
    summary,
    completionStatus,
    completionSummary,
    observedAt,
    analysisId,
  };
}

function messageTextContent(message: MessageClass): string {
  if (message.isAssistantMessage()) {
    return message.getTextOutput() ?? '';
  }
  const textParts = message.parts
    .filter((part) => part.isTextPart())
    .map((part) => part.text);
  return textParts.join('\n');
}

function toolPartState(toolPart: ToolPart): string {
  const state = toolPart.toolPart.state;
  return state ?? 'unknown';
}

function serializeToolPart(
  toolPart: ToolPart,
  message: MessageClass,
  position: number
): ConversationInsightsToolCall {
  const messageId = message.id ?? '';
  return {
    toolCallId: toolPart.toolCallId,
    toolName: toolPart.toolKey().toString(),
    messageId,
    position,
    state: toolPartState(toolPart),
    input: toolPart.input,
    output: toolPart.getOutput() ?? null,
  };
}

export function buildTurnsFromMessages(
  messages: MessageClass[],
  resolveMessagePosition: (message: MessageClass, index: number) => number
): ConversationInsightsTurn[] {
  const indexed = messages.map((message, index) => ({
    message,
    position: resolveMessagePosition(message, index),
    index,
  }));
  indexed.sort((a, b) => a.position - b.position);

  const turns: ConversationInsightsTurn[] = [];
  let currentTurn: ConversationInsightsTurn = {
    turnIndex: 0,
    userMessageId: null,
    userMessagePosition: null,
    toolCalls: [],
  };

  for (const { message, position } of indexed) {
    if (message.isUserMessage() && !message.isHidden()) {
      if (
        currentTurn.userMessageId !== null ||
        currentTurn.toolCalls.length > 0
      ) {
        turns.push(currentTurn);
        currentTurn = {
          turnIndex: turns.length,
          userMessageId: null,
          userMessagePosition: null,
          toolCalls: [],
        };
      }
      currentTurn.userMessageId = message.id ?? null;
      currentTurn.userMessagePosition = position;
      continue;
    }

    if (!message.isAssistantMessage()) {
      continue;
    }

    for (const toolPart of message.getToolParts()) {
      currentTurn.toolCalls.push(
        serializeToolPart(toolPart, message, position)
      );
    }
  }

  if (currentTurn.userMessageId !== null || currentTurn.toolCalls.length > 0) {
    turns.push(currentTurn);
  }

  return turns;
}

export function buildCompactionsFromMessages(
  messages: MessageClass[],
  resolveMessagePosition: (message: MessageClass, index: number) => number
): ConversationInsightsCompaction[] {
  const compactions: ConversationInsightsCompaction[] = [];
  for (const [index, message] of messages.entries()) {
    if (!message.isCompactionMessage()) {
      continue;
    }
    compactions.push({
      messageId: message.id ?? '',
      position: resolveMessagePosition(message, index),
      createdAt: message.createdAt.toISOString(),
      content: messageTextContent(message),
    });
  }
  compactions.sort((a, b) => a.position - b.position);
  return compactions;
}

export function serializeConversationResult(
  resultMessage: MessageClass | null,
  getPositionForMessageId: (messageId: string) => number | undefined
): ConversationInsightsResult | null {
  if (resultMessage === null || !resultMessage.isAssistantMessage()) {
    return null;
  }
  const messageId = resultMessage.id ?? '';
  const position =
    messageId !== '' ? getPositionForMessageId(messageId) ?? 0 : 0;
  return {
    messageId,
    position,
    output: resultMessage.getOutput(),
    text: resultMessage.getTextOutput(),
  };
}

export function mapFactsForInsights(facts: Fact[]): ConversationInsightsFact[] {
  return facts.map((fact) => ({
    scope: fact.type,
    text: fact.text,
  }));
}

export function buildCountsFromTurns(
  messages: MessageClass[],
  turns: ConversationInsightsTurn[]
): ConversationInsightsCounts {
  const userMessages = messages.filter(
    (message) => message.isUserMessage() && !message.isHidden()
  ).length;
  const assistantMessages = messages.filter((message) =>
    message.isAssistantMessage()
  ).length;
  const toolCalls = turns.reduce(
    (total, turn) => total + turn.toolCalls.length,
    0
  );
  return { userMessages, assistantMessages, toolCalls };
}

export function applyReceiptsToMessages(
  messages: MessageClass[],
  receipts: readonly Receipt[]
): MessageClass[] {
  const receiptMap = new Map(
    receipts.map((receipt) => [receipt.toolCallId, receipt])
  );
  return applyReceiptsToToolParts(messages, receiptMap);
}

/** @internal Exported for tests � ensures sentiment metric names stay exhaustive. */
export const SENTIMENT_METRIC_NAMES = ChatSentimentMetricName;
